import type { Asset } from "@/features/assets/hooks/usePropertyAssetRegistry";

export interface SpecGroup {
  title: string;
  fields: { label: string; value: string | null }[];
}

const RENDERED_CATEGORY_KEYS = new Set([
  'voltage', 'amperage', 'phase', 'wires', 'frequency_hz', 'power_kw',
  'btu_capacity', 'size_tons', 'capacity_gallons', 'airflow_cfm',
  'gas_cfh_low_diff', 'gas_cfh_high_diff', 'refrigerant_type',
  'fuel_type', 'pressure_psi', 'efficiency_rating', 'condition', 'tonnage',
]);

const fmt = (v: unknown, suffix?: string) =>
  v == null || v === '' ? null : `${v}${suffix ? ` ${suffix}` : ''}`;

/** Build the per-category specification groups for the Specifications card. */
export function buildSpecGroups(asset: Asset): SpecGroup[] {
  const specGroups: SpecGroup[] = [
    {
      title: 'Electrical',
      fields: [
        { label: 'Voltage',   value: fmt(asset.voltage) },
        { label: 'Amperage',  value: fmt(asset.amperage) },
        { label: 'Phase',     value: fmt(asset.phase) },
        { label: 'Wires',     value: fmt(asset.wires) },
        { label: 'Frequency', value: fmt(asset.frequency_hz, 'Hz') },
        { label: 'Power',     value: fmt(asset.power_kw, 'kW') },
      ],
    },
    {
      title: 'Capacity',
      fields: [
        { label: 'BTU rating',     value: fmt(asset.btu_capacity) },
        { label: 'Tons',           value: fmt(asset.size_tons) },
        { label: 'Tank capacity',  value: fmt(asset.capacity_gallons, 'gal') },
        { label: 'Airflow',        value: fmt(asset.airflow_cfm, 'CFM') },
        { label: 'Gas (½″ diff)',  value: fmt((asset as any).gas_cfh_low_diff, 'CFH') },
        { label: 'Gas (2″ diff)',  value: fmt((asset as any).gas_cfh_high_diff, 'CFH') },
      ],
    },
    {
      title: 'Refrigeration & Fuel',
      fields: [
        { label: 'Refrigerant', value: fmt(asset.refrigerant_type) },
        { label: 'Fuel type',   value: fmt(asset.fuel_type) },
      ],
    },
    {
      title: 'Other',
      fields: [
        { label: 'Pressure',          value: fmt(asset.pressure_psi, 'PSI') },
        { label: 'Efficiency rating', value: fmt(asset.efficiency_rating) },
        { label: 'Condition',         value: fmt(asset.condition) },
      ],
    },
  ];

  const dynamicSpecs: { label: string; value: string | null }[] = [];
  if (asset.category_attributes && typeof asset.category_attributes === 'object') {
    Object.entries(asset.category_attributes).forEach(([key, val]) => {
      if (!RENDERED_CATEGORY_KEYS.has(key) && val !== null && val !== undefined && val !== '') {
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        const valueString = typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val);
        dynamicSpecs.push({ label, value: valueString });
      }
    });
  }

  if (dynamicSpecs.length > 0) {
    specGroups.push({
      title: 'Category Details',
      fields: dynamicSpecs,
    });
  }

  return specGroups
    .map((g) => ({ ...g, fields: g.fields.filter((f) => f.value != null) }))
    .filter((g) => g.fields.length > 0);
}
